import {
  cacheKeys,
  Issue,
  useDeleteMutation,
  useIssue,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  IssueEdit,
  LoadingIndicator,
  SlideAction,
  Spacing,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUnsavedChangesBlocker } from '../../shared/hooks/useUnsavedChangesBlocker';

const DELETE_ISSUE = `
mutation deleteIssue($input: MutateIssueInput!) {
  deleteIssue(input: $input) {
    id
  }
}
`;

export const ActivityEditColumn = ({
  selectedLabels,
  onLabelsChange,
  resetRef,
}: {
  selectedLabels?: Array<{ id: string; name: string }>;
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
  resetRef?: MutableRefObject<(() => void) | undefined>;
}) => {
  const { issueId } = useParams();
  const { issue: issueData, isLoading } = useIssue(issueId ?? '');
  const issue = issueData as unknown as Issue | undefined;
  const { onSlideChange, toggleThirdPanel, setColumnOverlay } =
    useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDeleteMutation({
    modelName: 'issue',
    query: DELETE_ISSUE,
  });

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const onSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const handleFormStateChange = useCallback(
    (isDirty: boolean, isValid: boolean, onSave: () => Promise<void>) => {
      setFormIsDirty(isDirty);
      setFormIsValid(isValid);
      onSaveRef.current = onSave;
    },
    []
  );

  useUnsavedChangesBlocker({
    isDirty: formIsDirty,
    isValid: formIsValid,
    onSave: () => onSaveRef.current(),
  });

  const onReassignPress = async () => {
    if (location.pathname !== `/activities/${issue?.id}/reassign`) {
      await onSlideChange(SlideAction.NEXT);
      navigate(`/activities/${issue?.id}/reassign`);
    }
  };

  const onReviseSubmissionPress = async () => {
    if (location.pathname !== `/activities/${issue?.id}/revise`) {
      await onSlideChange(SlideAction.NEXT);
      navigate(`/activities/${issue?.id}/revise`);
    }
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!issue) {
    return null;
  }

  const hideDeleteOverlay = () => setColumnOverlay(null);

  const handleDeleteConfirm = async () => {
    if (isPending) return;
    await mutateAsync(
      { input: { id: issue.id } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('workspaceasset').all,
          });
          hideDeleteOverlay();
          navigate('/activities');
        },
      }
    );
  };

  const handleDeletePress = () => {
    setColumnOverlay(
      <View style={overlayStyles.overlay}>
        <View style={overlayStyles.sheet}>
          <Text style={overlayStyles.title}>{t('issue.delete.button')}</Text>
          <Text style={overlayStyles.subtitle}>
            {t('issue.delete.warning')}
          </Text>
          <TouchableOpacity
            style={overlayStyles.confirmButton}
            onPress={handleDeleteConfirm}
          >
            <Text style={overlayStyles.confirmButtonText}>
              {t('issue.menu.delete')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={overlayStyles.cancelButton}
            onPress={hideDeleteOverlay}
          >
            <Text style={overlayStyles.cancelText}>
              {t('issue.delete.cancel')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <IssueEdit
      issue={issue}
      issueId={issue.id}
      currentTitle={issue.title}
      workspaceId={issue.workspace?.id}
      currentLabels={issue.labels}
      currentAdjustCreationDate={issue.adjustCreationDate}
      currentCreatedAt={issue.createdAt}
      bottomBar={(props: any) => {
        if (resetRef) resetRef.current = props.onRightPress;
        return <View style={styles.footer}>
          <Button
            style={styles.saveButton}
            onPress={props.rightPressDisabled ? undefined : props.onSave}
            state={props.rightPressDisabled ? 'default' : 'active'}
          >
            {t('issue.editModal.save')}
          </Button>
          <Button
            variant="rectangular"
            state="outline"
            style={styles.deleteButton}
            onPress={handleDeletePress}
          >
            {t('issue.delete.button')}
          </Button>
        </View>;
      }}
      onReassignPress={onReassignPress}
      onReviseSubmissionPress={onReviseSubmissionPress}
      onLabelSelectorEdit={toggleThirdPanel}
      externalLabels={selectedLabels}
      onLabelsChange={onLabelsChange}
      onFormStateChange={handleFormStateChange}
    />
  );
};

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.baseMargin.base,
    paddingVertical: 20,
    backgroundColor: Colors.white,
    flexDirection: 'column',
    alignItems: 'center',
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    width: '100%',
    height: 48,
    borderRadius: 2,
  },
  deleteButton: {
    marginBottom: 12,
    width: '100%',
    height: 48,
  },
});

const overlayStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Colors.white,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: Colors.black,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: Colors.grayText,
    textAlign: 'center',
    marginBottom: 20,
  },
  confirmButton: {
    backgroundColor: Colors.red,
    borderRadius: 2,
    height: 48,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  confirmButtonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    height: 48,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    borderRadius: 2,
  },
  cancelText: {
    fontSize: 16,
    color: Colors.black,
  },
});
